'use client';

import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { SessionContext } from '@benzinga/session-context';
import {
  InsiderTradesManager,
  type InsidersTradeFiling,
  type InsidersRequestOptions,
} from '@benzinga/insider-trades-manager';
import { Table } from '@benzinga/table';

import { PermissionOverlay } from '../../../edge/dashboard/components/PermissionOverlay';
import { getRecentInsiderColumnDefs } from './columnDefs/recentInsiderColumnsDef';
import { TRADE_SIZE_OPTIONS, TRADE_TYPE_OPTIONS, type TradeSizeFilterValue } from './filterOptions';
import { InsiderFilterSelect } from './InsiderFilterSelect';
import { useInsiderEdgeGate } from './useInsiderEdgeGate';

import styles from '../../insiderTrades.module.scss';
import '../../table.scss';
import './agGridRegister';

/** Maps UI volume band to insider-trades POST body (absolute $ traded_value). */
function volumeBandToTradedValueParams(
  band: TradeSizeFilterValue,
): Partial<Pick<InsidersRequestOptions, 'traded_value_min' | 'traded_value_max'>> {
  switch (band) {
    case 'all':
      return {};
    case 'lt10k':
      return { traded_value_max: 10_000 };
    case 'gte10k':
      return { traded_value_min: 10_000 };
    case 'gte50k':
      return { traded_value_min: 50_000 };
    case 'gte100k':
      return { traded_value_min: 100_000 };
    case 'gte1m':
      return { traded_value_min: 1_000_000 };
    default:
      return {};
  }
}

export function RecentInsiderTradingTable() {
  const [transaction, setTransaction] = useState<(typeof TRADE_TYPE_OPTIONS)[number]['value']>('all');
  const [volumeBand, setVolumeBand] = useState<TradeSizeFilterValue>('all');
  const [filings, setFilings] = useState<InsidersTradeFiling[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const session = useContext(SessionContext);
  const insiderTradesManager = session.getManager(InsiderTradesManager);
  const { canLoadGatedData, isPaywalled } = useInsiderEdgeGate();

  const fetchRecent = useCallback(async () => {
    setLoading(true);
    setError(null);
    const tradeType = TRADE_TYPE_OPTIONS.find(o => o.value === transaction);
    const trade_types = tradeType && tradeType.value !== 'all' ? [tradeType.value] : undefined;
    const volumeParams = volumeBandToTradedValueParams(volumeBand);

    const options: InsidersRequestOptions = {
      trade_types,
      ...volumeParams,
    };

    try {
      const result = await insiderTradesManager.getInsiderTrades(options);
      if (result.ok) {
        setFilings(result.ok.filings.filings ?? []);
      } else {
        setError(result.err?.message ?? 'Failed to load');
        setFilings([]);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
      setFilings([]);
    } finally {
      setLoading(false);
    }
  }, [transaction, volumeBand, insiderTradesManager]);

  useEffect(() => {
    if (isPaywalled) {
      setFilings([]);
      setError(null);
      setLoading(false);
      return;
    }
    if (!canLoadGatedData) return;
    fetchRecent();
  }, [canLoadGatedData, fetchRecent, isPaywalled]);

  const columnDefs = useMemo(() => getRecentInsiderColumnDefs(), []);

  const tableBody = (
    <div
      className={`${styles['insider-table-section-body']} ${styles['insider-trades-table-wrapper']} insider-trades-table-wrapper`}
    >
      {error && (
        <div className={styles['top-companies-empty']} role="alert">
          {error}
        </div>
      )}
      {loading && filings.length === 0 ? (
        <div className={styles['top-companies-empty']}>Loading…</div>
      ) : (
        <>
          <Table<InsidersTradeFiling>
            rowData={filings}
            columnsDef={columnDefs as React.ComponentProps<typeof Table<InsidersTradeFiling>>['columnsDef']}
            isGated={isPaywalled}
            gatedRowBlur="row"
            gatedVisibleRowCount={0}
            noDataText={isPaywalled ? 'Unlock Benzinga Edge to view insider trades.' : undefined}
          />
          {!loading && !isPaywalled && filings.length === 0 && !error && (
            <div className={styles['top-companies-empty']}>No recent filings for these filters.</div>
          )}
        </>
      )}
    </div>
  );

  return (
    <div className={styles['insider-table-section']}>
      <div className={styles['insider-table-section-header']}>
        <h3 className={styles['section-title']}>Recent Insider Trading</h3>
        <div className={styles['insider-table-filters']}>
          <InsiderFilterSelect
            id="recent-volume"
            label="Trade value"
            ariaLabel="Trade value filter"
            value={volumeBand}
            disabled={isPaywalled}
            onValueChange={v => setVolumeBand(v as TradeSizeFilterValue)}
            options={TRADE_SIZE_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
          />
          <InsiderFilterSelect
            id="recent-transaction"
            label="Transaction"
            ariaLabel="Transaction type"
            value={transaction}
            disabled={isPaywalled}
            onValueChange={v => setTransaction(v as (typeof TRADE_TYPE_OPTIONS)[number]['value'])}
            options={TRADE_TYPE_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
          />
          {/* <InsiderFilterSelect
            id="recent-role"
            label="Insider Role"
            ariaLabel="Insider role"
            value={role}
            onValueChange={v => setRole(v as InsiderRoleFilterValue)}
            options={INSIDER_ROLE_OPTIONS}
          /> */}
        </div>
      </div>

      <PermissionOverlay>{tableBody}</PermissionOverlay>
    </div>
  );
}
